/**
 * SuperAdminFeatureFlags.jsx — Control global de módulos por tenant/plan.
 * Ruta: /superadmin/feature-flags
 */
import { useEffect, useState, useCallback } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import { fetchGlobalFlags, updateGlobalFlags } from "@/services/superadminApi";
import { RefreshCw, Save, Zap, ToggleLeft, ToggleRight, AlertTriangle } from 'lucide-react';

const FLAG_META = {
    qr_orders: { label: "Pedidos QR", desc: "Permite pedidos via código QR desde mesa", category: "Pedidos" },
    delivery: { label: "Delivery", desc: "Módulo completo de entregas a domicilio", category: "Logística" },
    drivers: { label: "Repartidores", desc: "Panel de gestión de repartidores propios", category: "Logística" },
    marketing: { label: "Marketing", desc: "Herramientas de campañas y cupones avanzados", category: "Marketing" },
    crm: { label: "CRM", desc: "Gestión de clientes, fidelización y comportamiento", category: "Marketing" },
    analytics: { label: "Analítica Avanzada", desc: "Reportes y dashboards de negocio", category: "Datos" },
    multi_branch: { label: "Multi-sucursal", desc: "Gestión de varias ubicaciones bajo un mismo tenant", category: "Estructura" },
    smart_printing: { label: "Impresión Inteligente", desc: "Impresión silenciosa, agentes y comandas automáticas", category: "Operaciones" },
    loyalty: { label: "Fidelización", desc: "Programas de puntos y recompensas para clientes", category: "Marketing" },
    reservations: { label: "Reservas de Mesa", desc: "Sistema de reservas y gestión de capacidad", category: "Pedidos" },
    kiosk_mode: { label: "Modo Kiosk", desc: "Pantalla de autopedido para el cliente final", category: "Operaciones" },
};

const CATEGORIES = [...new Set(Object.values(FLAG_META).map(f => f.category))];

function FlagToggle({ flagKey, value, onChange }) {
    const meta = FLAG_META[flagKey] || { label: flagKey, desc: "", category: "Otro" };

    return (
        <div style={{
            background: 'rgba(15,23,42,0.6)',
            border: `1px solid ${value ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.06)'}`,
            borderRadius: 16,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            transition: 'border-color 0.2s',
            cursor: 'pointer',
        }} onClick={() => onChange(flagKey, !value)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                    width: 40, height: 40, borderRadius: 12,
                    background: value ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.04)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `1px solid ${value ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.06)'}`,
                    transition: 'all 0.2s', flexShrink: 0,
                }}>
                    <Zap size={16} style={{ color: value ? '#818cf8' : 'rgba(255,255,255,0.25)' }} />
                </div>
                <div>
                    <p style={{ fontSize: 14, fontWeight: 700, color: value ? 'white' : 'rgba(255,255,255,0.55)', marginBottom: 3 }}>
                        {meta.label}
                    </p>
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>{meta.desc}</p>
                </div>
            </div>
            <div style={{ flexShrink: 0 }}>
                {value
                    ? <ToggleRight size={28} style={{ color: '#6366f1' }} />
                    : <ToggleLeft size={28} style={{ color: 'rgba(255,255,255,0.2)' }} />
                }
            </div>
        </div>
    );
}

export default function SuperAdminFeatureFlags() {
    const [flags, setFlags] = useState({});
    const [original, setOriginal] = useState({});
    const [availableFlags, setAvailableFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const load = useCallback(() => {
        setLoading(true);
        setError("");
        fetchGlobalFlags()
            .then(r => {
                setFlags(r.flags || {});
                setOriginal(r.flags || {});
                setAvailableFlags(r.available_flags || Object.keys(r.flags || {}));
            })
            .catch(() => setError("No se pudieron cargar los feature flags."))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleToggle = (key, val) => {
        setFlags(f => ({ ...f, [key]: val }));
        setSuccess("");
    };

    const hasChanges = JSON.stringify(flags) !== JSON.stringify(original);

    const handleSave = async () => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const changed = {};
            for (const k of availableFlags) {
                if (flags[k] !== original[k]) changed[k] = flags[k];
            }
            await updateGlobalFlags(changed);
            setOriginal({ ...flags });
            setSuccess("✓ Feature flags globales actualizados correctamente.");
        } catch (err) {
            setError(err.response?.data?.detail || "Error al guardar los cambios.");
        } finally {
            setSaving(false);
        }
    };

    const allKeys = availableFlags.length > 0 ? availableFlags : Object.keys(FLAG_META);

    return (
        <SuperAdminLayout>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <h1 style={{ fontSize: 28, fontWeight: 900, color: 'white', letterSpacing: '-0.5px', marginBottom: 6 }}>Feature Flags Globales</h1>
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 15 }}>
                        Control de módulos por defecto para todos los tenants de la plataforma
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={load} style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <RefreshCw size={14} />Actualizar
                    </button>
                    {hasChanges && (
                        <button onClick={handleSave} disabled={saving} style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', borderRadius: 12, padding: '10px 20px', fontWeight: 700, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Save size={14} />{saving ? "Guardando..." : "Guardar cambios"}
                        </button>
                    )}
                </div>
            </div>

            {hasChanges && (
                <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#fbbf24', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 600 }}>
                    <AlertTriangle size={16} />Tienes cambios sin guardar. Haz clic en "Guardar cambios" para aplicarlos.
                </div>
            )}

            {error && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>⚠️ {error}</span>
                    <button onClick={load} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <RefreshCw size={12} />Reintentar
                    </button>
                </div>
            )}

            {success && (
                <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#10b981', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, fontWeight: 600 }}>
                    {success}
                </div>
            )}

            {loading ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                        <div key={i} style={{ height: 78, background: 'rgba(255,255,255,0.04)', borderRadius: 16, animation: 'pulse 1.5s ease infinite' }} />
                    ))}
                </div>
            ) : (
                <>
                    {CATEGORIES.map(cat => {
                        const catFlags = allKeys.filter(k => (FLAG_META[k]?.category || 'Otro') === cat);
                        if (catFlags.length === 0) return null;
                        return (
                            <div key={cat} style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 13, fontWeight: 800, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12 }}>
                                    {cat}
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 10 }}>
                                    {catFlags.map(key => (
                                        <FlagToggle
                                            key={key}
                                            flagKey={key}
                                            value={!!flags[key]}
                                            onChange={handleToggle}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}

                    {/* Flags sin categoría conocida */}
                    {allKeys.filter(k => !FLAG_META[k]).length > 0 && (
                        <div style={{ marginBottom: 32 }}>
                            <h3 style={{ fontSize: 13, fontWeight: 800, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12 }}>
                                Otros módulos
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 10 }}>
                                {allKeys.filter(k => !FLAG_META[k]).map(key => (
                                    <FlagToggle key={key} flagKey={key} value={!!flags[key]} onChange={handleToggle} />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: 16, padding: '16px 20px', marginTop: 16 }}>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>
                    <strong style={{ color: 'rgba(255,255,255,0.8)' }}>ℹ️ Nota:</strong> Estos son los valores <strong>por defecto</strong> para todos los tenants.
                    Los overrides individuales por tenant (configurados en la ficha de cada restaurante) <strong>tienen mayor prioridad</strong> y no se ven afectados por cambios aquí.
                    Los cambios de plan también pueden sobreescribir estos valores según las features del plan asignado.
                </p>
            </div>
        </SuperAdminLayout>
    );
}
